import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncList, useLastKnownTotal, useStableValue, useTableState } from "@/ds";
import {
  DEFAULT_AUDIT_SORT,
  hasAuditFilters,
  isAuditSortField,
  listAuditRecords,
} from "@/modules/audit";
import type { AuditFilters, AuditRecord, AuditSort, AuditSortField } from "@/modules/audit";
import { AUDIT_ERROR_MESSAGE, AUDIT_FORBIDDEN_MESSAGE } from "../audit.constants";
import { filtrosDoAno } from "../audit.utils";

interface ListaParams {
  filters: AuditFilters;
  page: number;
  pageSize: number;
  sort: AuditSort;
}

/**
 * Model da listagem: filtros, página e ordenação no servidor; mudar um critério volta à página 1 (RN004). Abre no ano
 * corrente, o padrão do filtro Ano.
 */
export function useAuditLista(anoCorrente: number) {
  const [padrao] = useState(() => filtrosDoAno(anoCorrente));
  const [rawFilters, setFilters] = useState<AuditFilters>(padrao);
  const filters = useStableValue(rawFilters);

  const { lastTotal, remember } = useLastKnownTotal();
  const tabela = useTableState<AuditSortField>({
    isSortField: isAuditSortField,
    total: lastTotal,
    resetKey: JSON.stringify(filters),
  });
  // Sem coluna escolhida a consulta segue a ordem padrão (Data/Hora, mais recente) e nenhum cabeçalho mostra a seta.
  const appliedSort: AuditSort = tabela.sort ?? DEFAULT_AUDIT_SORT;

  const lista = useAsyncList<ListaParams, AuditRecord>({
    fetcher: async (params, signal) => {
      const { records, total } = await listAuditRecords({
        ...params.filters,
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        signal,
      });
      return { items: records, total };
    },
    params: { filters, page: tabela.page + 1, pageSize: tabela.rows, sort: appliedSort },
    forbiddenMessage: AUDIT_FORBIDDEN_MESSAGE,
    errorMessage: AUDIT_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
  remember(lista.status, lista.total);

  return {
    filters,
    appliedSort,
    /** Há critério além do padrão (o ano corrente sem os demais filtros). */
    hasFilters: hasAuditFilters(filters, padrao),
    items: lista.items,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    tabela,
    /** Os critérios do painel entram em conjunto (RN002). */
    applyFilters: setFilters,
    /** Volta ao padrão: o ano corrente, sem os demais critérios (RN003). */
    clearFilters: () => setFilters(padrao),
  };
}

export type UseAuditListaResult = ReturnType<typeof useAuditLista>;
